import { LIVE_POSITION_WINDOW_SECONDS } from "@genius-tracker/config";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/api.js";
import { heavyPollIntervalMs } from "../lib/apiBackoff.js";
import { drainCursorPagesBounded } from "../lib/queryPageDrain.js";
import { usePageVisibility } from "../lib/usePageVisibility.js";
import { useRealtimeContext } from "../realtime/RealtimeProvider.js";

/**
 * Sprint 28 Wave C — fetch the latest tenant positions so the Live
 * Map can render each vehicle marker rotated to its true course
 * (req #3 — no direction arrows; the icon itself rotates to
 * heading).
 *
 * Reads `GET /v1/telemetry/positions/latest`. The endpoint is
 * tenant-scoped and RLS-clean; it returns one row per vehicle with
 * `headingDeg` (nullable). The customer Live Map polls this on the
 * same cadence as `useLiveVehicles`: a long safety-net interval
 * when the realtime WS is `live`, a short fallback when it isn't.
 *
 * Output shape: a stable `Map<vehicleId, { headingDeg, recordedAt }>`
 * so consumers can look up a heading without scanning an array each
 * render.
 *
 * The look-back window is fixed to `LIVE_POSITION_WINDOW_SECONDS` and is
 * deliberately NOT a caller option — see the note at the assignment. For a
 * caller-chosen historical window use `usePositionHistory`, which reads the
 * separate `/history` endpoint built for exactly that.
 */

export interface PositionSnapshot {
  readonly vehicleId: string;
  readonly recordedAt: string;
  readonly lat: number;
  readonly lng: number;
  readonly speedKph: number | null;
  readonly headingDeg: number | null;
  /**
   * GT-LIVE-MAP-POSITION-VALIDITY-SATELLITES-BATTERY-BUILD-01 (GT-OPEN-121,
   * DL-206) — the protocol's own verdict on this sample's GNSS fix.
   * `unknown` covers rows written before the platform recorded one; it is
   * never treated as `valid`. Optional so an older API response still parses.
   */
  readonly fixValidity?: "valid" | "invalid" | "unknown" | null;
  /**
   * Observed satellite count. A real `0` means "zero satellites were seen";
   * `null`/absent means "the protocol did not report a count". The UI must
   * render those two differently.
   */
  readonly satellites?: number | null;
  /**
   * GT-OPEN-121 — vehicle/external supply voltage in volts measured at THIS
   * snapshot's `recordedAt`. `null`/absent means no trustworthy sample exists
   * for that exact instant, which the UI renders as `—`. It is deliberately
   * position-scoped: a voltage from another moment would look plausible while
   * describing a different observation.
   */
  readonly supplyVoltage?: number | null;
  /**
   * GT-LIVEMAP-TRIPS-BUGFIXES (B4) — fuel level at THIS snapshot's instant,
   * with the unit it was measured in.
   *
   * The unit travels with the value rather than being assumed per tenant: a
   * single fleet mixes Genius Tracker One (percent) with Teltonika units that
   * can report litres, and a column that guessed would show a 78 % tank as
   * 78 litres.
   */
  readonly fuelLevel?:
    | { readonly value: number; readonly unit: "%" | "L" }
    | { readonly state: "not_calibrated" }
    | null;
}

export type LatestPositionMap = ReadonlyMap<string, PositionSnapshot> & {
  /** True when the defensive cursor budget left current positions unread. */
  readonly isTruncated: boolean;
};

interface ListLatestPositionsResponse {
  readonly items: ReadonlyArray<PositionSnapshot>;
  readonly serverTime: string;
  readonly nextCursor: string | null;
}

export const LATEST_POSITIONS_KEY = ["telemetry", "positions", "latest"] as const;

const LATEST_POSITIONS_PAGE_BUDGET = 5;

export async function loadLatestPositionPages(
  sinceSeconds: number,
  limit: number,
  signal: AbortSignal,
): Promise<ListLatestPositionsResponse> {
  let lastResponse: ListLatestPositionsResponse | undefined;
  const drained = await drainCursorPagesBounded(async (cursor) => {
    const response = await apiGet<ListLatestPositionsResponse>(
      "/v1/telemetry/positions/latest",
      { sinceSeconds, limit, ...(cursor === undefined ? {} : { cursor }) },
      signal,
    );
    lastResponse = response;
    return response;
  }, LATEST_POSITIONS_PAGE_BUDGET);

  if (lastResponse === undefined) {
    throw new Error("Latest positions pagination returned no page.");
  }

  // Partial on purpose: a non-null cursor tells the hook's consumers that the
  // page budget capped the result, instead of failing the whole live map.
  return {
    items: drained.items,
    serverTime: lastResponse.serverTime,
    nextCursor: drained.truncated ? (drained.lastPage.nextCursor ?? null) : null,
  };
}

export function useLatestPositions(opts?: {
  /**
   * Required: only run the query when the caller has decided this is
   * the right time to fetch. Sprint 28 Wave C uses this to gate on
   * (a) the rotation feature flag and (b) the caller's
   * `telemetry.read` permission, so no role that lacks the
   * permission ends up polling a 403. (Codex P1 #351.)
   */
  enabled?: boolean;
  limit?: number;
  fallbackIntervalMs?: number;
  liveIntervalMs?: number;
}) {
  // GT-LIVEMAP-TRIPS-BUGFIXES (B1) — the look-back is NOT a caller
  // decision. Three call sites each passed their own `sinceSeconds: 600`
  // (10 min) while the Connection badge classified `online` from a longer
  // threshold, so every vehicle in the gap rendered "Online" beside empty
  // speed / voltage / satellite cells. Removing the parameter entirely —
  // rather than fixing its default — means a caller cannot reintroduce the
  // mismatch, because there is nothing left to pass.
  const sinceSeconds = LIVE_POSITION_WINDOW_SECONDS;
  const limit = opts?.limit ?? 500;
  const enabled = opts?.enabled ?? true;
  const fallbackIntervalMs = opts?.fallbackIntervalMs ?? 3000;
  const liveIntervalMs = opts?.liveIntervalMs ?? 60_000;
  const visible = usePageVisibility();
  const { status } = useRealtimeContext();
  const isLive = status === "live";
  const intervalMs = isLive ? liveIntervalMs : fallbackIntervalMs;

  return useQuery<ListLatestPositionsResponse, Error, LatestPositionMap>({
    queryKey: [...LATEST_POSITIONS_KEY, { sinceSeconds, limit }],
    queryFn: ({ signal }) => loadLatestPositionPages(sinceSeconds, limit, signal),
    enabled,
    select: (data) => {
      const out = new Map<string, PositionSnapshot>();
      for (const p of data.items) {
        out.set(p.vehicleId, p);
      }
      return Object.assign(out, { isTruncated: data.nextCursor !== null }) as LatestPositionMap;
    },
    staleTime: 0,
    gcTime: 5 * 60_000,
    // GT-…-FIX-07 — defer the next poll past an active heavy-read backoff
    // window (pause polling instead of re-hammering the limiter).
    refetchInterval: visible && enabled ? () => heavyPollIntervalMs(intervalMs) : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}
